import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signIn, SignInOutput, getCurrentUser } from 'aws-amplify/auth';
import { useUserStore } from '@/stores/userStore';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const enableBackend = useUserStore((state) => state.enableBackend);
  const setProfileId = useUserStore((state) => state.setProfileId);
  const fetchProfile = useUserStore((state) => state.fetchProfile);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result: SignInOutput = await signIn({
        username: email,
        password,
      });

      if (result.isSignedIn) {
        // Get the authenticated user's ID
        const user = await getCurrentUser();

        // Extract username from email or use preferred_username
        const username = user.signInDetails?.loginId?.split('@')[0] || 'User';

        // Update profile to use Cognito user ID and username
        setProfileId(user.userId, username);

        // Enable backend sync
        enableBackend();

        // Fetch the user's profile from backend to get correct XP and level
        await fetchProfile();

        // Redirect to home
        navigate('/');
      } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        setError('Please confirm your email address before signing in.');
      }
    } catch (err: any) {
      console.error('Sign in error:', err);
      if (err.name === 'UserNotFoundException' || err.name === 'NotAuthorizedException') {
        setError('Invalid email or password');
      } else if (err.name === 'UserNotConfirmedException') {
        setError('Please confirm your email address before signing in.');
      } else {
        setError(err.message || 'Failed to sign in');
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-black p-4">
      <div className="max-w-md w-full bg-black border border-primary-500/30 rounded-lg shadow-[0_0_30px_rgba(34,197,94,0.2)] p-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary-400 mb-2 drop-shadow-[0_0_15px_rgba(34,197,94,0.5)]">
            CodeQuest
          </h1>
          <p className="text-primary-200/70">Sign in to continue your journey</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-primary-300 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-black border border-primary-500/30 rounded-lg text-primary-200 placeholder-primary-200/40 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/50 transition-all"
              placeholder="you@example.com"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-primary-300 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-black border border-primary-500/30 rounded-lg text-primary-200 placeholder-primary-200/40 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/50 transition-all"
              placeholder="••••••••"
              required
            />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-primary-500 text-black py-3 rounded-lg font-semibold hover:bg-primary-600 hover:drop-shadow-[0_0_20px_rgba(34,197,94,0.6)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-primary-200/70">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary-400 hover:text-primary-300 font-medium hover:drop-shadow-[0_0_8px_rgba(34,197,94,0.5)] transition-all">
              Sign up
            </Link>
          </p>
        </div>

        <div className="mt-8 pt-6 border-t border-primary-500/20 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-primary-200/60 hover:text-primary-400 text-sm transition-all"
          >
            Continue without signing in (local mode)
          </button>
        </div>
      </div>
    </div>
  );
}
